import { getAccessToken } from "./utils";


export const cleanDataLayerPayload = (payload) => {
  if (!payload || typeof payload !== "object") return payload;
  const cleaned = {};
  for (const [key, value] of Object.entries(payload)) {
    if (Array.isArray(value)) {
      cleaned[key] = value.map(item => cleanDataLayerPayload(item));
    } else if (value !== "NA" && value !== "na" && value !== null && value !== undefined && value !== "") {
      cleaned[key] = value;
    }
  }
  return cleaned;
};

export const getFlowId = () => {
  if (typeof window === "undefined") return "NA";

  // Check if the user is an active subscriber
  const { uuid } = getAuthStatus();
  if (uuid && uuid !== "NA") {
    const activePlanName = sessionStorage.getItem(`active_plan_name_${uuid}`);
    if (activePlanName && activePlanName !== "NA") {
      return "NA"; // Do not generate flow_id for active subscribers
    }
  }

  let flowId = sessionStorage.getItem("user_flow_id");
  if (!flowId) {
    flowId = typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Math.random().toString(36).substring(2, 15) + "-" + Date.now();
    sessionStorage.setItem("user_flow_id", flowId);
  }

  return flowId;
};

export const getArticleContext = () => {
  let articleContext = { article_id: "NA", section_name: "NA", premium_article: "NA" };
  if (typeof window === "undefined") return articleContext;
  try {
    const savedContext = sessionStorage.getItem("last_article_context");
    if (savedContext) {
      const parsed = JSON.parse(savedContext);
      if (parsed.article_id) articleContext.article_id = parsed.article_id;
      if (parsed.section_name) articleContext.section_name = parsed.section_name;
      if (parsed.premium_article !== undefined) articleContext.premium_article = parsed.premium_article;
    }
  } catch (err) {}
  return articleContext;
};

export const fullyDecodeURI = (uri) => {
  let decoded = uri;
  try {
    while (decoded !== decodeURIComponent(decoded)) {
      decoded = decodeURIComponent(decoded);
    }
  } catch (e) {}
  return decoded;
};

export const getGaClientId = () => {
  if (typeof window === "undefined") return "NA";
  const match = document.cookie.match(/(?:^|;)\s*_ga=([^;]*)/);
  if (match && match[1]) {
    const parts = match[1].split(".");
    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  }
  return "NA";
};

export const getGaSessionId = () => {
  if (typeof window === "undefined") return "NA";

  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    // A GA4 session cookie starts with _ga_ followed by the container ID
    if (cookie.startsWith("_ga_")) {
      const cookieValue = cookie.split("=")[1];
      if (cookieValue) {
        const decodedValue = decodeURIComponent(cookieValue);

        // Handle newer GS2.1 format
        if (decodedValue.startsWith("GS2.1.")) {
          const parts = decodedValue.split(".");
          if (parts.length >= 3) {
            const payload = parts[2];
            if (payload) {
              const sessionPart = payload.split("$").find(p => p.startsWith("s"));
              if (sessionPart) {
                return sessionPart.substring(1);
              }
            }
          }
        }

        // Handle older GS1.1 format
        if (decodedValue.startsWith("GS1.1.")) {
          const parts = decodedValue.split(".");
          if (parts.length >= 3) {
            return parts[2];
          }
        }
      }
    }
  }

  return "NA";
};

export const pushSubscribeClickEvent = (source = "NA") => {
  if (typeof window === "undefined") return;

  const { status, uuid } = getAuthStatus();
  const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;

  const articleContext = getArticleContext();

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload({
    event: "subscribe_click",
    auth_status: formattedStatus,
    uuid: uuid,
    page_path: fullyDecodeURI(window.location.pathname + window.location.search),
    subscribe_source: source,
    flow_id: getFlowId(),
    article_id: articleContext.article_id,
    section_name: articleContext.section_name,
    premium_article: articleContext.premium_article
  }));
};

export const pushViewPlansEvent = (plans = [], source = "NA") => {
  if (typeof window === "undefined" || !plans || plans.length === 0) return;

  const { status, uuid } = getAuthStatus();
  const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;

  const articleContext = getArticleContext();

  const items = plans.map((plan, index) => {
    let category = "NA";

    // Safely parse duration logic directly from the API object
    if (plan.duration_unit) {
      const unit = (plan.duration_unit || "").toLowerCase();
      const length = plan.duration_length || 1;

      if (unit.includes("year")) category = "yearly";
      else if (unit.includes("month")) {
        if (length === 12) category = "yearly";
        else category = length === 1 ? "monthly" : `${length}-months`;
      } else if (unit.includes("day")) {
        category = length === 1 ? "daily" : `${length}-days`;
      }
    }

    // Fallback just in case duration unit is missing
    if (category === "NA" && (plan.plan_name || plan.title)) {
      const name = (plan.plan_name || plan.title || "").toLowerCase();
      if (name.includes("year")) category = "yearly";
      else if (name.includes("month")) category = "monthly";
      else if (name.includes("day")) category = "daily";
    }

    const priceCents = plan.discounted_price_cents ?? plan.price_cents ?? plan.plan_amount_cents ?? 0;

    return {
      item_id: String(plan.id || ""),
      item_name: plan.title || plan.plan_name || "NA",
      item_category: category,
      price: priceCents / 100,
      currency: plan.price_currency || "INR",
      index: index + 1
    };
  });

  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: "view_plans",
    auth_status: formattedStatus,
    uuid: uuid,
    item_list_name: "subscribe_page",
    items: items.map(item => Object.fromEntries(Object.entries(item).filter(([_, v]) => v !== "NA"))),
    subscribe_source: source,
    flow_id: getFlowId(),
    article_id: articleContext.article_id,
    section_name: articleContext.section_name,
    premium_article: articleContext.premium_article
  };

  window.dataLayer.push(cleanDataLayerPayload(payload));
};

export const resolveCampaign = (campaign) => {
  if (campaign && campaign !== "NA" && campaign !== "na") return campaign;
  if (typeof window !== "undefined") {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const c = searchParams.get("utm_campaign") || searchParams.get("campaign");
      if (c && c !== "NA" && c !== "na") return c;
    } catch (e) {}
  }
  return undefined;
};

export const pushBeginCheckoutEvent = (plan = null, source = "NA", campaign = null, loginInFlow = false, index = 1) => {
  if (typeof window === "undefined" || !plan) return;

  const { status, uuid } = getAuthStatus();
  const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;
  const campaignVal = resolveCampaign(campaign);

  let articleContext = { article_id: "NA", section_name: "NA", premium_article: "NA" };
  try {
    const savedContext = sessionStorage.getItem("last_article_context");
    if (savedContext) {
      const parsed = JSON.parse(savedContext);
      if (parsed.article_id) articleContext.article_id = parsed.article_id;
      if (parsed.section_name) articleContext.section_name = parsed.section_name;
      if (parsed.premium_article !== undefined) articleContext.premium_article = parsed.premium_article;
    }
  } catch (err) {}

  let category = "NA";
  if (plan.duration_unit) {
    const unit = (plan.duration_unit || "").toLowerCase();
    const length = plan.duration_length || 1;
    if (unit.includes("year")) category = "yearly";
    else if (unit.includes("month")) {
      if (length === 12) category = "yearly";
      else category = length === 1 ? "monthly" : `${length}-months`;
    } else if (unit.includes("day")) {
      category = length === 1 ? "daily" : `${length}-days`;
    }
  }

  if (category === "NA" && (plan.plan_name || plan.title)) {
    const name = (plan.plan_name || plan.title || "").toLowerCase();
    if (name.includes("year")) category = "yearly";
    else if (name.includes("month")) category = "monthly";
    else if (name.includes("day")) category = "daily";
  }

  const priceCents = plan.discounted_price_cents ?? plan.price_cents ?? plan.plan_amount_cents ?? 0;
  const price = priceCents / 100;
  const planName = plan.title || plan.plan_name || "NA";

  const items = [{
    item_id: String(plan.id || ""),
    item_name: planName,
    item_category: category,
    price: price,
    currency: plan.price_currency || "INR",
    index: index
  }];

  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: "begin_checkout",
    auth_status: formattedStatus,
    plan_name: planName,
    plan_id: String(plan.id || ""),
    plan_price: price,
    currency: plan.price_currency || "INR",
    items: items.map(item => Object.fromEntries(Object.entries(item).filter(([_, v]) => v !== "NA"))),
    subscribe_source: source,
    flow_id: getFlowId(),
    article_id: articleContext.article_id,
    section_name: articleContext.section_name,
    premium_article: articleContext.premium_article,
    login_in_flow: loginInFlow
  };

  if (formattedStatus === "logged_in" && uuid && uuid !== "NA") {
    payload.uuid = uuid;
  }
  if (campaignVal) {
    payload.campaign = campaignVal;
  }

  window.dataLayer.push(cleanDataLayerPayload(payload));
};

export const pushSelectPlanEvent = (plan, source = "NA", index = 1, campaign = null, loginInFlow = null) => {
  if (typeof window === "undefined" || !plan) return;

  const { status, uuid } = getAuthStatus();

  const formattedStatus = status === "not_logged_in" ? "non_logged_in" : status;
  const campaignVal = resolveCampaign(campaign);

  const articleContext = getArticleContext();

  let category = "NA";
  if (plan.duration_unit) {
    const unit = (plan.duration_unit || "").toLowerCase();
    const length = plan.duration_length || 1;
    if (unit.includes("year")) category = "yearly";
    else if (unit.includes("month")) {
      if (length === 12) category = "yearly";
      else category = length === 1 ? "monthly" : `${length}-months`;
    } else if (unit.includes("day")) {
      category = length === 1 ? "daily" : `${length}-days`;
    }
  }
  if (category === "NA" && (plan.plan_name || plan.title)) {
    const name = (plan.plan_name || plan.title || "").toLowerCase();
    if (name.includes("year")) category = "yearly";
    else if (name.includes("month")) category = "monthly";
    else if (name.includes("day")) category = "daily";
  }

  const priceCents = plan.discounted_price_cents ?? plan.price_cents ?? plan.plan_amount_cents ?? 0;
  const price = priceCents / 100;

  const item = {
    item_id: String(plan.id || ""),
    item_name: plan.title || plan.plan_name || "NA",
    item_category: category,
    price: price,
    currency: plan.price_currency || "INR",
    index: index
  };

  const effectiveLoginInFlow = loginInFlow !== null
    ? Boolean(loginInFlow)
    : (formattedStatus !== "logged_in" || Boolean(
        sessionStorage.getItem("login_in_this_checkout_flow") ||
        sessionStorage.getItem("sso_login_initiated_source")
      ));

  const eventPayload = {
    event: "select_plan",
    auth_status: formattedStatus,
    value: price,
    currency: plan.price_currency || "INR",
    plan_name: plan.title || plan.plan_name || "NA",
    plan_id: String(plan.id || ""),
    plan_price: price,
    items: [item],
    subscribe_source: source,
    flow_id: getFlowId(),
    article_id: articleContext.article_id,
    section_name: articleContext.section_name,
    premium_article: articleContext.premium_article,
    login_in_flow: effectiveLoginInFlow
  };

  if (formattedStatus === "logged_in" && uuid && uuid !== "NA") {
    eventPayload.uuid = uuid;
  }
  if (campaignVal) {
    eventPayload.campaign = campaignVal;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(eventPayload));
};

export const pushLoginInitiatedEvent = (source = "NA", campaign = null) => {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.setItem("login_initiated_active", "true");
  } catch (e) {}

  const articleContext = getArticleContext();
  const campaignVal = resolveCampaign(campaign);

  const payload = {
    event: "login_initiated",
    auth_status: "anonymous",
    subscribe_source: source,
    page_path: fullyDecodeURI(window.location.pathname + window.location.search),
    article_id: articleContext.article_id,
    premium_article: articleContext.premium_article,
    section_name: articleContext.section_name,
    flow_id: getFlowId()
  };

  if (campaignVal) {
    payload.campaign = campaignVal;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(payload));
};



export const pushLoginCompletedEvent = (source = "NA", campaign = null) => {
  if (typeof window === "undefined") return;

  // Guard: login_completed should ONLY trigger if login_initiated was fired in this flow
  let wasInitiated = false;
  try {
    wasInitiated = sessionStorage.getItem("login_initiated_active") === "true";
  } catch (e) {}

  if (!wasInitiated) {
    console.warn("[DataLayer] login_completed blocked: No preceding login_initiated event.");
    return;
  }

  // Consume flag so it cannot fire multiple times
  try {
    sessionStorage.removeItem("login_initiated_active");
  } catch (e) {}

  const { status, uuid } = getAuthStatus();
  if (status !== "logged_in") return;

  const articleContext = getArticleContext();
  const campaignVal = resolveCampaign(campaign);

  let loginMethod = "";
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const data = JSON.parse(userStr);
      loginMethod = data?.metadata?.login_method;
    }
  } catch (e) {
    console.error("Error parsing user data", e);
  }

  const eventPayload = {
    event: "login_completed",
    auth_status: "logged_in",
    login_method: loginMethod,
    flow_id: getFlowId(),
    subscribe_source: source,
    article_id: articleContext.article_id,
    section_name: articleContext.section_name,
    premium_article: articleContext.premium_article,
    page_path: fullyDecodeURI(window.location.pathname + window.location.search)
  };

  if (uuid && uuid !== "NA") {
    eventPayload.uuid = uuid;
  }
  if (campaignVal) {
    eventPayload.campaign = campaignVal;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload(eventPayload));
};

export const getAuthStatus = () => {
  if (typeof window === "undefined") return { status: "anonymous", uuid: "NA" };

  try {
    const userStr = localStorage.getItem("user");
    const accessToken = getAccessToken() || (typeof window !== "undefined" && window.localStorage?.getItem("access_token"));
    const refreshToken = typeof window !== "undefined" && window.localStorage?.getItem("refresh_token");

    if (userStr && (accessToken || refreshToken)) {
      const parsedUser = JSON.parse(userStr);
      let uuid = parsedUser?.userData?.id || parsedUser?.id || "NA";

      if (uuid !== "NA") {
        uuid = String(uuid).substring(0, 36);
      }

      return { status: "logged_in", uuid };
    }
  } catch (error) {
    console.error("Error reading auth from localStorage", error);
  }

  return { status: "anonymous", uuid: "NA" };
};

const convertTimestampToYYYYMMDDHHMM = (timestamp) => {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return "NA";

  return (
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0") +
    " " +
    String(date.getHours()).padStart(2, "0") +
    ":" +
    String(date.getMinutes()).padStart(2, "0")
  );
};

const getCommentCount = () => {
  if (typeof window !== "undefined") {
    const commentCountElement = document.querySelector(".coral-count");
    if (commentCountElement) {
      const text = commentCountElement.innerText.trim();
      const count = Number(text);
      return isNaN(count) ? "NA" : count;
    }
  }
  return "NA";
};

const sanitizeUrl = (url) => {
  if (!url || typeof url !== "string") return "NA";
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.delete("access_token");
    urlObj.searchParams.delete("refresh_token");
    return urlObj.toString();
  } catch (e) {
    return url
      .replace(/([?&])access_token=[^&]*/gi, "$1access_token=REDACTED")
      .replace(/([?&])refresh_token=[^&]*/gi, "$1refresh_token=REDACTED");
  }
};

let storyTimeout = null;
let lastAuthTrackTime = 0;
let lastAuthTrackUser = "";

export const pushPageDataLayer = (pageType, userData = {}, pageData = {}) => {
  if (typeof window === "undefined") return;

  if (storyTimeout) {
    clearTimeout(storyTimeout);
    storyTimeout = null;
  }

  if (pageType === "not-found" || pageType === "404") {
    const uuid = userData?.userData?.id ? String(userData.userData.id).substring(0, 36) : "NA";
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(cleanDataLayerPayload({
      event: "page_view",
      page_type: "404",
      auth_status: uuid !== "NA" ? "logged_in" : "anonymous",
      uuid
    }));
    return;
  }

  const dataLayerObject = {
    event: "page_view",
    page_type: "NA",
    section_name: "NA",
    premium_article: "no",
    access_level_value: "NA"
  };

  if (pageType === "home-page" || pageType === "home") {
    dataLayerObject.page_type = "home_page";
  } else if (pageType === "subscribe-page") {
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const q = searchParams.get("q");
      if (q === "review") dataLayerObject.page_type = "proceed_to_pay_page";
      else if (q === "success") dataLayerObject.page_type = "success_page";
      else if (q === "failure") dataLayerObject.page_type = "failure_page";
      else if (q === "subscribe" || !q) dataLayerObject.page_type = "subscribe_plan_page";
      else dataLayerObject.page_type = "subscribe_plan_page";
    } catch (e) {
      dataLayerObject.page_type = "subscribe_plan_page";
    }
  } else if (pageType === "static-page") {
    dataLayerObject.page_type = "static_page";
  } else if (pageType === "story-page" || pageType === "article") {
    dataLayerObject.page_type = "article_page";

    if (pageData?.story) {
      const story = pageData.story;
      dataLayerObject.author_name = story["author-name"] || story.author || "NA";
      dataLayerObject.content_id = story.id || "NA";
      dataLayerObject.article_id = story["public-identifier"] || story["story-template"] || story.id || "NA";
      dataLayerObject.article_type = story["story-template"] || "NA";
      dataLayerObject.author_id = story.authors?.map((author) => author.id).join(",") || "NA";
      dataLayerObject.content_title = story.headline || "NA";
      dataLayerObject.creator_name = story["owner-name"] || "NA";

      if (story["content-created-at"]) {
        dataLayerObject.created_date = convertTimestampToYYYYMMDDHHMM(story["content-created-at"]);
      }
      dataLayerObject.story_tags = story.tags ? story.tags.map((t) => t.name).join(",") : "NA";

      const isPremium = story?.access === "subscription" || story?.["access-level-value"] === "subscription";
      dataLayerObject.premium_article = isPremium ? "yes" : "no";

      // Prioritize access-level-value. If it's null or undefined, fallback to "NA"
      if (story["access-level-value"] !== null && story["access-level-value"] !== undefined) {
        dataLayerObject.access_level_value = String(story["access-level-value"]);
      } else {
        dataLayerObject.access_level_value = "NA";
      }

      dataLayerObject.story_words = story["word-count"] || "NA";

      if (story["first-published-at"] || story["published-at"]) {
        dataLayerObject.published_date = convertTimestampToYYYYMMDDHHMM(
          story["first-published-at"] || story["published-at"]
        );
      }
      if (story["last-published-at"]) {
        dataLayerObject.last_updated_date = convertTimestampToYYYYMMDDHHMM(story["last-published-at"]);
      }
      if (story.sections && story.sections.length > 0) {
        dataLayerObject.section_name = story.sections.map((s) => s.name).join(",");
      }

      try {
        const articleContext = {
          author_name: dataLayerObject.author_name,
          content_id: dataLayerObject.content_id,
          article_id: dataLayerObject.article_id,
          article_type: dataLayerObject.article_type,
          author_id: dataLayerObject.author_id,
          content_title: dataLayerObject.content_title,
          creator_name: dataLayerObject.creator_name,
          created_date: dataLayerObject.created_date,
          story_tags: dataLayerObject.story_tags,
          premium_article: isPremium ? "yes" : "no",
          access_level_value: dataLayerObject.access_level_value,
          story_words: dataLayerObject.story_words,
          published_date: dataLayerObject.published_date,
          last_updated_date: dataLayerObject.last_updated_date,
          section_name: dataLayerObject.section_name,
          comment_number: dataLayerObject.comment_number
        };
        sessionStorage.setItem("last_article_context", JSON.stringify(articleContext));
      } catch (err) {}
    }
  } else if (pageType === "section-page") {
    if (pageData?.section) {
      const section = pageData.section;
      dataLayerObject.page_type = section["parent-id"] ? "sub_section_page" : "section_page";
      dataLayerObject.section_name = section["display-name"] || section.name || "NA";
    }
  } else if (pageType === "search") {
    dataLayerObject.page_type = "search_page";
  } else if (pageType === "author-page" || pageType === "author") {
    dataLayerObject.page_type = "author_page";
    if (pageData?.author) {
      dataLayerObject.author_name = pageData.author.name || "NA";
    }
  } else if (pageType === "tag-page" || pageType === "tag") {
    dataLayerObject.page_type = "tag_page";
  } else {
    dataLayerObject.page_type = "static_page";
  }

  const pushToDataLayer = () => {
    let currentUserData = userData;
    if (!currentUserData || (!currentUserData?.userData?.id && !currentUserData?.id)) {
      try {
        const stored = typeof window !== "undefined" && window.localStorage?.getItem("user");
        if (stored) {
          currentUserData = JSON.parse(stored);
        }
      } catch (e) {}
    }
    const rawId = currentUserData?.userData?.id || currentUserData?.id;
    let uuid = rawId ? String(rawId).substring(0, 36) : "NA";
    let authStatus = uuid !== "NA" ? "logged_in" : "anonymous";

    if (uuid === "NA") {
      const authFromStorage = getAuthStatus();
      uuid = authFromStorage.uuid;
      authStatus = authFromStorage.status;
    }

    dataLayerObject.auth_status = authStatus;
    if (authStatus === "logged_in" && uuid && uuid !== "NA") {
      dataLayerObject.uuid = uuid;
    }

    if (
      pageType === "subscribe-page" ||
      dataLayerObject.page_type === "subscribe_plan_page" ||
      dataLayerObject.page_type === "proceed_to_pay_page" ||
      dataLayerObject.page_type === "success_page" ||
      dataLayerObject.page_type === "failure_page"
    ) {
      delete dataLayerObject.premium_article;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(cleanDataLayerPayload(dataLayerObject));

    if (authStatus === "logged_in") {
      const skipAuthTracking = sessionStorage.getItem("skip_auth_tracking");
      if (skipAuthTracking === "true") {
        sessionStorage.removeItem("skip_auth_tracking");
      } else {
        addAuthTrackingToDataLayer(currentUserData?.userData?.id ? currentUserData : { userData: { id: uuid } });
      }
    }
  };

  if (pageType === "story-page" || pageType === "article") {
    storyTimeout = setTimeout(() => {
      dataLayerObject.comment_number = getCommentCount();
      pushToDataLayer();
      storyTimeout = null;
    }, 1500);
  } else {
    pushToDataLayer();
  }
};

export const pushEventToDataLayer = (eventName) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload({ event: eventName }));
};

let lastSignUpLoginTrackTime = 0;
let lastSignUpLoginTrackUser = null;


export const addSignUpLoginToDataLayer = (url, data) => {
  if (typeof window === "undefined" || !data) return;

  const userId = data?.userData?.id || data?.id || "NA";
  const now = Date.now();
  if (userId !== "NA" && userId === lastSignUpLoginTrackUser && now - lastSignUpLoginTrackTime < 1000) {
    return;
  }
  lastSignUpLoginTrackTime = now;
  lastSignUpLoginTrackUser = userId;

  const loginSource = sanitizeUrl(url);

  const marketingConsentStatus = data?.metadata?.receiveMarketingAndPromitionalEmail || false;

  const accountCreationDate = data?.userData?.timeJoined
    ? convertTimestampToYYYYMMDDHHMM(data.userData.timeJoined)
    : "NA";

  const loginMethod =
    data?.metadata?.login_method === "phone_login"
      ? "mobile"
      : data?.metadata?.login_method === "google_login"
        ? "google"
        : data?.metadata?.login_method === "email_login"
          ? "email"
          : "unknown";

  const createdAt = new Date(data?.userData?.timeJoined).getTime();
  const eventType = Date.now() - createdAt < 60000 ? "signup" : "login";

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload({
    event: eventType,
    login_source: loginSource,
    marketing_consent_status: marketingConsentStatus,
    account_creation_date: accountCreationDate,
    user_id: userId,
    authentication_method: loginMethod
  }));
};

export const addLogoutToDataLayer = (url, data, source = "profile") => {
  if (typeof window === "undefined") return;

  const { uuid } = getAuthStatus();
  let subscriptionStatus = "free";
  if (uuid !== "NA") {
    const activePlanName = window.sessionStorage.getItem(`active_plan_name_${uuid}`);
    if (activePlanName && activePlanName !== "NA") subscriptionStatus = "active";
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload({
    event: "logout",
    login_source: sanitizeUrl(url),
    user_id: data?.userData?.id || "NA",
    auth_status: "anonymous",
    source: source,
    uuid: uuid,
    subscription_status: subscriptionStatus
  }));
};

export const addAuthTrackingToDataLayer = (data) => {
  if (typeof window === "undefined") return;

  const userId = data?.userData?.id || data?.id || "NA";
  const now = Date.now();
  if (userId !== "NA" && userId === lastAuthTrackUser && now - lastAuthTrackTime < 500) {
    return;
  }
  lastAuthTrackTime = now;
  lastAuthTrackUser = userId;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload({
    event: "auth",
    user_id: userId,
    auth_status: "authenticated"
  }));
};

export const addClickTrackingToDataLayer = (url, text, id) => {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(cleanDataLayerPayload({
    event: "widget_click",
    click_url: url || "NA",
    click_text: text || "NA",
    click_id: id || "NA"
  }));
};

const RELEVANT_USER_PROPERTIES = [
  "auth_status",
  "subscription_status",
  "uuid",
  "account_created_date",
  "plan_name",
  "plan_id",
  "plan_price"
];

let lastUserPropertiesState = null;

const getPreviousUserProperties = () => {
  if (lastUserPropertiesState) return lastUserPropertiesState;
  if (typeof window !== "undefined") {
    try {
      const stored = window.sessionStorage?.getItem("last_user_properties_pushed");
      if (stored) {
        lastUserPropertiesState = JSON.parse(stored);
        return lastUserPropertiesState;
      }
    } catch (e) {}
  }
  return null;
};

const saveCurrentUserProperties = (state) => {
  lastUserPropertiesState = state;
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage?.setItem("last_user_properties_pushed", JSON.stringify(state));
    } catch (e) {}
  }
};

const haveUserPropertiesChanged = (prevState, nextState) => {
  if (!prevState || !nextState) return true;
  for (const key of RELEVANT_USER_PROPERTIES) {
    const prevVal = prevState[key];
    const nextVal = nextState[key];
    if (prevVal === undefined && nextVal === undefined) continue;
    if (prevVal === undefined || nextVal === undefined) return true;
    if (String(prevVal) !== String(nextVal)) return true;
  }
  return false;
};

export const pushUserPropertiesUpdateToDataLayer = (userData, isSubscriber, planName = "NA", planPrice = "NA", planId = "NA") => {
  if (typeof window === "undefined") return;

  const dataLayerObject = {
    event: "user_properties_update"
  };

  const authFromStorage = getAuthStatus();

  if (authFromStorage.status === "anonymous") {
    dataLayerObject.auth_status = "anonymous";
    dataLayerObject.subscription_status = "free";
  } else {
    dataLayerObject.auth_status = "logged_in";
    dataLayerObject.uuid = authFromStorage.uuid;

    const timeJoined = userData?.userData?.timeJoined || userData?.timeJoined;
    if (timeJoined) {
      dataLayerObject.account_created_date = new Date(timeJoined).toISOString();
    } else {
      try {
        const userStr = window.localStorage.getItem("user");
        if (userStr) {
          const parsedUser = JSON.parse(userStr);
          const localTime = parsedUser?.userData?.timeJoined || parsedUser?.timeJoined;
          if (localTime) {
            dataLayerObject.account_created_date = new Date(localTime).toISOString();
          }
        }
      } catch (e) {}
    }

    const normalizedPrice = (planPrice !== "NA" && planPrice !== null && planPrice !== undefined && !isNaN(Number(planPrice)))
      ? Number(planPrice)
      : planPrice;
    const normalizedPlanId = (planId !== "NA" && planId !== null && planId !== undefined && !isNaN(Number(planId)))
      ? Number(planId)
      : planId;

    if (isSubscriber === "expired") {
      dataLayerObject.subscription_status = "expired";
      dataLayerObject.plan_name = planName;
      dataLayerObject.plan_price = normalizedPrice;
      dataLayerObject.plan_id = normalizedPlanId;
    } else if (isSubscriber === "active" || isSubscriber === true) {
      dataLayerObject.subscription_status = "active";
      dataLayerObject.plan_name = planName;
      dataLayerObject.plan_price = normalizedPrice;
      dataLayerObject.plan_id = normalizedPlanId;
    } else {
      dataLayerObject.subscription_status = "free";
    }
  }

  const currentRelevantState = {};
  for (const key of RELEVANT_USER_PROPERTIES) {
    if (dataLayerObject[key] !== undefined) {
      currentRelevantState[key] = dataLayerObject[key];
    }
  }

  const previousState = getPreviousUserProperties();
  if (!haveUserPropertiesChanged(previousState, currentRelevantState)) {
    return;
  }
  saveCurrentUserProperties(currentRelevantState);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(dataLayerObject);
};

